import React, { lazy, Suspense } from 'react'
import { useTranslation } from 'react-i18next'
import type { useInfoSheet } from '@fs/zion-ui'
import { InfoSheet, Paragraph, usePageWidth, Bleed, MarginBox } from '@fs/zion-ui'
import { useGroupsContext } from '@fs/zion-tree-groups'
import useFocusPerson, { mutateFocusPerson } from '../../../data-service/useFocusPerson'
import { useContributionsContext } from '../../../providers/ContributionsProvider'

const SourceList = lazy(() => import('@fs/zion-source-list'))

type Props = {
  infoSheet: ReturnType<typeof useInfoSheet>
}

export default function SourcesInfoSheet({ infoSheet }: Props): JSX.Element | null {
  const [t] = useTranslation()
  const { focusPerson } = useFocusPerson()
  const infoSheetSize = usePageWidth()({ default: 'sm', xxl: 'md' })
  const { currentTreeId } = useGroupsContext()
  const { refetchContributions } = useContributionsContext()
  if (!focusPerson) return null

  const onSourceChanged = (): void => {
    // This is to update source count on focusPerson
    mutateFocusPerson(focusPerson.id, currentTreeId || 'PRIVATE')
    refetchContributions()
  }

  return (
    <InfoSheet
      {...infoSheet}
      size={infoSheetSize}
      title={`${t('tree-person.sources.header')} (${focusPerson.sourceCount})`}
      data-testid="sources-infosheet"
    >
      <Bleed all>
        <MarginBox marginX="nano" marginY="nano">
          <Suspense fallback="">
            {(infoSheet.isOpen || infoSheet.transitioning) && focusPerson && (
              <SourceList
                entityId={focusPerson.id}
                entityType="person"
                hideOptions
                hideTableHeaders
                disableSourceAddButton={focusPerson.readOnly}
                sourceAddedCallback={onSourceChanged}
                sourceRemovedCallback={onSourceChanged}
                sourceTagOverlayProps={{ closeOnClickAway: false }}
              />
            )}
          </Suspense>
        </MarginBox>
      </Bleed>
      {focusPerson.readOnly && focusPerson.sourceCount === 0 && (
        <Paragraph>{t('tree-person.read-only-sources.message')}</Paragraph>
      )}
    </InfoSheet>
  )
}
